import {
  DataPointProps,
  LineInBarChartPropsType,
} from '@euler/lib/charts-core';
import React from 'react';
import { Animated, View } from 'react-native';
import Svg, { Path } from 'react-native-svg';
import { isIos, isWebApp } from '../../../utils';
import { renderDataPoints } from './renderDataPoints';
import { renderSpecificDataPoints } from './renderSpecificDataPoints';
import { renderSpecificVerticalLines } from './renderSpecificVerticalLines';

const RenderLineInBarChart = (props: LineInBarChartPropsType) => {
  const {
    yAxisLabelWidth,
    initialSpacing,
    spacing,
    containerHeight,
    lineConfig,
    maxValue,
    animatedWidth,
    lineBehindBars,
    points,
    arrowPoints,
    data,
    totalWidth,
    barWidth,
    labelsExtraHeight,
    xAxisLabelsVerticalShift,
    selectedIndex,
    containerHeightIncludingBelowXAxis,
    yAxisOffset,
    highlightEnabled,
    lowlightOpacity,
    disableForeignObject,
  } = props;

  const firstBarWidth = data[0].barWidth ?? barWidth;
  const opacity = highlightEnabled
    ? selectedIndex.includes?.(-1)
      ? 1
      : lowlightOpacity
    : 1;

  const svgHeight = containerHeightIncludingBelowXAxis + labelsExtraHeight;

  const svgHeightProp = isWebApp ? svgHeight : undefined;

  const dataPointsProps: DataPointProps = {
    data,
    lineConfig,
    barWidth,
    containerHeight,
    maxValue,
    firstBarWidth,
    yAxisLabelWidth,
    spacing,
    selectedIndex: selectedIndex[0],
    yAxisOffset,
    opacity,
    svgHeight,
    totalWidth,
    disableForeignObject,
  };

  const specificVerticalLinesProps = {
    data,
    barWidth,
    yAxisLabelWidth,
    initialSpacing,
    spacing,
    containerHeight,
    lineConfig,
    maxValue,
  };

  const specificDataPointsProps = {
    data,
    barWidth,
    firstBarWidth,
    yAxisLabelWidth,
    lineConfig,
    spacing,
    containerHeight,
    maxValue,
    yAxisOffset,
    disableForeignObject,
  };

  const renderAnimatedLine = () => {
    return (
      <Animated.View
        pointerEvents={isIos ? 'none' : 'box-none'} // in iOS box-none doesn't work as expected
        style={{
          position: 'absolute',
          height: svgHeight,
          left: 6 - yAxisLabelWidth,
          bottom: -10 + xAxisLabelsVerticalShift, //stepHeight * -0.5 + xAxisThickness,
          width: animatedWidth,
          zIndex: lineBehindBars ? -1 : 100000,
          // backgroundColor: 'wheat',
        }}
      >
        <Svg
          pointerEvents={isIos ? 'none' : 'box-none'}
          height={svgHeightProp}
          width={isWebApp ? animatedWidth : undefined}
        >
          <Path
            d={points}
            fill="none"
            opacity={opacity}
            stroke={lineConfig.color}
            strokeWidth={lineConfig.thickness}
            strokeDasharray={lineConfig.strokeDashArray}
          />

          {renderSpecificVerticalLines(specificVerticalLinesProps)}

          {!lineConfig.hideDataPoints
            ? renderDataPoints(dataPointsProps)
            : renderSpecificDataPoints(specificDataPointsProps)}
          {lineConfig.showArrow && (
            <Path
              d={arrowPoints}
              fill={lineConfig.arrowConfig?.fillColor}
              stroke={lineConfig.arrowConfig?.strokeColor}
              strokeWidth={lineConfig.arrowConfig?.strokeWidth}
            />
          )}
        </Svg>
      </Animated.View>
    );
  };

  const renderLine = () => {
    return (
      <View
        pointerEvents={isIos ? 'none' : 'box-none'} // in iOS box-none doesn't work as expected
        style={{
          position: 'absolute',
          height: svgHeight,
          left: 6 - yAxisLabelWidth,
          bottom: -10 + xAxisLabelsVerticalShift, //stepHeight * -0.5 + xAxisThickness,
          width: totalWidth,
          zIndex: lineBehindBars ? -1 : 100000,
          // backgroundColor: 'rgba(200,150,150,0.1)'
        }}
      >
        <Svg
          pointerEvents={isIos ? 'none' : 'box-none'}
          height={svgHeightProp}
          width={isWebApp ? totalWidth : undefined}
        >
          <Path
            d={points}
            fill="none"
            opacity={opacity}
            stroke={lineConfig.color}
            strokeWidth={lineConfig.thickness}
            strokeDasharray={lineConfig.strokeDashArray}
          />
          {renderSpecificVerticalLines(specificVerticalLinesProps)}

          {!lineConfig.hideDataPoints
            ? renderDataPoints(dataPointsProps)
            : renderSpecificDataPoints(specificDataPointsProps)}
          {lineConfig.showArrow && (
            <Path
              d={arrowPoints}
              fill={lineConfig.arrowConfig?.fillColor}
              stroke={lineConfig.arrowConfig?.strokeColor}
              strokeWidth={lineConfig.arrowConfig?.strokeWidth}
            />
          )}
        </Svg>
      </View>
    );
  };

  if (lineConfig.isAnimated) {
    return renderAnimatedLine();
  }

  return renderLine();
};

export default RenderLineInBarChart;
